import {
    Box,
    Button,
    Card,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    Select,
    SelectChangeEvent,
    Stack,
    styled,
    Switch,
    Typography,
    MenuItem,
    Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal, { CommonModalProps } from "..";
import UploadCard, { UploadCardHandle, UploadContext } from "../../Cards/UploadCard/upload-card";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { AppTheme, DbUserInfos, NotificationUserSubscribe, ShowMode } from "../../../Core/Api/Types/db-user-infos";
import { produce } from "immer";
import { cacheService, userService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { UpdatePasswordRequest } from "../../../Core/Api/Types/update-password-request";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { UserThemeContext } from "../../../Utils/Helpers/userTheme";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import FormInputPassword from "../../Forms/FormInputPassword/form-input-password";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { Language } from "../../../i18n";
import moment from "moment";

type CloseClbProps = {
    confirm: boolean;
};

export type ModalUserSettingsElementProps = {
    onClose?: (props: CloseClbProps) => void;
};
export type ModalUserSettingsProps = CommonModalProps<ModalUserSettingsElementProps>;

const ModalUserSettings = React.forwardRef<any, ModalUserSettingsProps>((props, ref) => {
    const { t, i18n } = useTranslation();
    const notifications = [
        { k: NotificationUserSubscribe.ShareFirstView, label: t("modal:modalUserSettings.notification.sharedIsViewed") },
        {
            k: NotificationUserSubscribe.DownloadLinkTransfer,
            label: t("modal:modalUserSettings.notification.linkIsDownloaded"),
        },
    ];

    //const dragOrderLists = React.useMemo(() => cacheService.GetDragOrderLists(), []);
    const [dragOrderLists, setDragOrderLists] = useState(cacheService.GetDragOrderLists());
    //   const [columnOrdersTable, setColumnOrdersTable] = useState(cacheService.GetColOrder());

    const { closeModal, onClose, ...modalProps } = props;
    const { active: changePasswordFormVisible, toggle: toggleChangePasswordFormVisible } = useToggle(false);
    const {
        selected: notificationsChecked,
        isSelected: isNotificationChecked,
        select: checkNotification,
        selectAll: checkAllNotification,
        unselectAll: uncheckAllNotification,
    } = useMultipleSelection<string>(
        cacheService.GetUserInfo()!.prefs?.notifications || [],
        notifications.map((x) => x.k)
    );

    const {
        selected: featuresChecked,
        isSelected: isFeatureChecked,
        select: checkFeature,
        selectAll: checkAllFeature,
        unselectAll: uncheckAllFeature,
    } = useMultipleSelection<string>(cacheService.GetActiveFeatures() || undefined, GlobalConst.UI_FEATURES);

    const isAllNotificationsChecked = React.useMemo(
        () => notificationsChecked.length === notifications.length,
        [notificationsChecked, notifications]
    );

    const isAllFeaturesChecked = React.useMemo(
        () => featuresChecked.length === GlobalConst.UI_FEATURES.length,
        [featuresChecked, GlobalConst.UI_FEATURES.length]
    );

    const [user, setUser] = React.useState<DbUserInfos>(cacheService.GetUserInfo()!);
    const [requestPwd, setRequestPwd] = React.useState<UpdatePasswordRequest>(new UpdatePasswordRequest());
    const enqueueCustomSnackbar = useCustomSnackbar();
    const uploadPhotoRef = React.useRef<UploadCardHandle>(null);

    const themeContext = React.useContext(UserThemeContext);
    const { userTheme, setUserTheme } = { ...themeContext };

    const formId = `password-edit`;
    const formValid = useAppSelector(GetFormIsValid(formId));

    const onChangeAllNotifications = React.useCallback(
        (checked: boolean) => {
            if (checked) {
                return checkAllNotification();
            }
            return uncheckAllNotification();
        },
        [checkAllNotification, uncheckAllNotification]
    );

    const onChangeAllFeatures = React.useCallback(
        (checked: boolean) => {
            if (checked) {
                return checkAllFeature();
            }
            return uncheckAllFeature();
        },
        [checkAllFeature, uncheckAllFeature]
    );

    const handleCancelClick = () => {
        onClose && onClose({ confirm: false });
        closeModal();
    };

    const handleConfirmClick = () => {
        // update user
        userService.UpdateUser({ userInfos: user!, userCustomersLinks: [] }).then((response) => {
            enqueueCustomSnackbar(t("alert:success.settingsUpdated"), "success");
            cacheService.SetUser(user);
        });
        cacheService.SetActiveFeatures(featuresChecked);
        onClose && onClose({ confirm: true });
        closeModal();
    };

    const handleChangePassword = () => {
        userService.UpdatePasswordBySession(requestPwd).then((response) => {
            if (response.success) enqueueCustomSnackbar(t("alert:success.passwordChanged"), "success");
            else enqueueCustomSnackbar(response.message || t("alert:error.changingPassword"), "error");
        });
        onClose && onClose({ confirm: true });
        closeModal();
    };

    const handleChangeTheme = (event: SelectChangeEvent) => {
        const theme = event.target.value as string;
        setUserTheme && setUserTheme(theme);

        let appTheme: AppTheme = theme === "light" ? AppTheme.Light : AppTheme.Dark;
        let audioTracksList = isFeatureChecked("Audio Tracks List");

        setUser(
            produce((draft) => {
                if (!draft.prefs)
                    draft.prefs = {
                        theme: appTheme,
                        showMode: ShowMode.Thumbs,
                        audioTracksList: audioTracksList,
                        notifications: notificationsChecked.map((notification) => NotificationUserSubscribe[notification]),
                        lang: i18n.language as Language,
                    };
                else draft.prefs.theme = appTheme;
            })
        );
    };

    const handleChangeLanguage = (event: SelectChangeEvent) => {
        i18n.changeLanguage(event.target.value);
        moment.locale(i18n.language);

        let audioTracksList = isFeatureChecked("Audio Tracks List");
        let appTheme: AppTheme = userTheme === "light" ? AppTheme.Light : AppTheme.Dark;

        setUser(
            produce((draft) => {
                if (!draft.prefs)
                    draft.prefs = {
                        theme: appTheme,
                        showMode: ShowMode.Thumbs,
                        audioTracksList: audioTracksList,
                        notifications: notificationsChecked.map((notification) => NotificationUserSubscribe[notification]),
                        lang: event.target.value as Language,
                    };
                else draft.prefs.lang = event.target.value as Language;
            })
        );
    };

    const handleChangeShowMode = (event: SelectChangeEvent) => {
        const mode = event.target.value as string;

        let showMode: ShowMode = mode === "Table" ? ShowMode.Table : ShowMode.Thumbs;
        let audioTracksList = isFeatureChecked("Audio Tracks List");
        let appTheme: AppTheme = userTheme === "light" ? AppTheme.Light : AppTheme.Dark;

        setUser(
            produce((draft) => {
                if (!draft.prefs)
                    draft.prefs = {
                        theme: appTheme,
                        showMode: showMode,
                        audioTracksList: audioTracksList,
                        notifications: notificationsChecked.map((notification) => NotificationUserSubscribe[notification]),
                        lang: i18n.language as Language,
                    };
                else draft.prefs.showMode = showMode;
            })
        );
    };

    useEffect(() => {
        let appTheme: AppTheme = userTheme === "light" ? AppTheme.Light : AppTheme.Dark;
        let audioTracksList = isFeatureChecked("Audio Tracks List");

        setUser(
            produce((draft) => {
                if (!draft.prefs)
                    draft.prefs = {
                        theme: appTheme,
                        showMode: ShowMode.Thumbs,
                        audioTracksList: audioTracksList,
                        notifications: notificationsChecked.map((notification) => NotificationUserSubscribe[notification]),
                        lang: i18n.language as Language,
                    };
                else {
                    draft.prefs.audioTracksList = audioTracksList;
                }
            })
        );
    }, [featuresChecked]);

    useEffect(() => {
        let appTheme: AppTheme = userTheme === "light" ? AppTheme.Light : AppTheme.Dark;
        let audioTracksList = isFeatureChecked("Audio Tracks List");

        setUser(
            produce((draft) => {
                if (!draft.prefs)
                    draft.prefs = {
                        theme: appTheme,
                        showMode: ShowMode.Thumbs,
                        audioTracksList: audioTracksList,
                        notifications: notificationsChecked.map((notification) => NotificationUserSubscribe[notification]),
                        lang: i18n.language as Language,
                    };
                else
                    draft.prefs.notifications = notificationsChecked.map(
                        (notification) => NotificationUserSubscribe[notification]
                    );
            })
        );
    }, [notificationsChecked]);

    const handleClearDragOrderList = (key: string) => {
        cacheService.SetDragListOrder(key, []);
        setDragOrderLists((x) => ({ ...x, [key]: [] }));
    };

    //   const handleClearcolumnOrdersTable = (key: string) => {
    //     cacheService.SetColsOrder(key, []);
    //     setColumnOrdersTable((x) => ({ ...x, [key]: [] }));
    //   };

    return (
        <Modal
            {...modalProps}
            title={t("common:userSettings")}
            reference={ref}
            closeModal={handleCancelClick}
            width={680}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmClick}>
                        {t("common:confirm")}
                    </Button>
                </Stack>
            }
        >
            <Typography variant="h5" mb={2}>
                {t("common:generalInformations")}
            </Typography>
            {/*  profile picture */}
            <Box>
                <UploadCard
                    title={t("card:title.updatePhoto")}
                    ref={uploadPhotoRef}
                    coverRounded
                    coverPlaceholderIcon={<AccountCircleOutlinedIcon />}
                    userHasRightToDelete={true}
                    uploadButtonInCard={true}
                    context={UploadContext.ProfilePicture}
                    objectId={0}
                    format={{
                        id: 1,
                        isUploaded: false,
                        maxFileSizeMb: 0.1,
                        name: "Picture",
                        userHasRighToDelete: false,
                        acceptsFiles: { ["application/octet-stream"]: [".jpg", ".jpeg", ".png"] },
                        description: t("card:description.myNewProfilePicture"),
                        isUserDataMandatory: false,
                        objectType: ObjectType.Image,
                    }}
                />
            </Box>
            <Grid container columnSpacing={3} rowSpacing={2} mt={0}>
                <Grid size={6}>
                    <FormControl sx={{ width: "100%" }}>
                        <FormInputText
                            label={t("common:firstName")}
                            fullWidth={true}
                            value={user?.firstName}
                            placeholder={t("common:firstName")}
                            onChange={(e) =>
                                setUser(
                                    produce((draft) => {
                                        if (e.currentTarget) draft!.firstName = e.currentTarget.value;
                                    })
                                )
                            }
                            validationProps={{
                                formId: "",
                                fieldId: "firstName",
                                required: false,
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl sx={{ width: "100%" }}>
                        <FormInputText
                            label={t("common:lastName")}
                            fullWidth={true}
                            value={user?.lastName}
                            placeholder={t("common:lastName")}
                            onChange={(e) =>
                                setUser(
                                    produce((draft) => {
                                        if (e.currentTarget) draft!.lastName = e.currentTarget.value;
                                    })
                                )
                            }
                            validationProps={{
                                formId: "",
                                fieldId: "lastName",
                                required: false,
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl sx={{ width: "100%" }}>
                        <FormInputText
                            label={t("common:emailAddress")}
                            fullWidth={true}
                            value={user?.email}
                            placeholder={t("common:emailAddress")}
                            onChange={(e) =>
                                setUser(
                                    produce((draft) => {
                                        if (e.currentTarget) draft!.email = e.currentTarget.value;
                                    })
                                )
                            }
                            validationProps={{
                                formId: "email",
                                fieldId: "title",
                                required: false,
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl sx={{ width: "100%" }}>
                        <FormInputText
                            label={t("common:phoneNumber")}
                            fullWidth={true}
                            value={user?.phone}
                            placeholder={t("common:phoneNumber")}
                            onChange={(e) =>
                                setUser(
                                    produce((draft) => {
                                        if (e.currentTarget) draft!.phone = e.currentTarget.value;
                                    })
                                )
                            }
                            validationProps={{
                                formId: "",
                                fieldId: "phone",
                                required: false,
                            }}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Box>
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{t("common:password")}</Typography>
                    {changePasswordFormVisible ? (
                        <Button variant="outlined" color="error" onClick={toggleChangePasswordFormVisible}>
                            {t("common:cancel")}
                        </Button>
                    ) : (
                        <Button startIcon={<LockOutlinedIcon />} onClick={toggleChangePasswordFormVisible}>
                            {t("button:changeMyPassword")}
                        </Button>
                    )}
                </Stack>
                <Collapse in={changePasswordFormVisible}>
                    <Grid container spacing={1} mt={2}>
                        <Grid size={4}>
                            <FormControl sx={{ width: "100%", mb: 2 }}>
                                <FormInputText
                                    label={t("field:label.oldPassword")}
                                    fullWidth={true}
                                    placeholder={t("field:label.oldPassword")}
                                    isPassword={true}
                                    onChange={(e) => {
                                        if (!e || !e.target || e.target === null) return;
                                        setRequestPwd(
                                            produce((draft) => {
                                                draft!.oldPassword = e.target.value;
                                            })
                                        );
                                    }}
                                    validationProps={{
                                        formId: "",
                                        fieldId: "oldPassword",
                                        required: false,
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl sx={{ width: "100%", mb: 2 }}>
                                <FormInputPassword
                                    label={t("common:newPassword")}
                                    placeholder={t("common:newPassword")}
                                    onChange={(e) => {
                                        if (!e || !e.target || e.target === null) return;

                                        setRequestPwd(
                                            produce((draft) => {
                                                draft!.password = e.target.value;
                                            })
                                        );
                                    }}
                                    validationProps={{
                                        formId: formId,
                                        fieldId: "newPassword",
                                        required: true,
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl sx={{ width: "100%", mb: 2 }}>
                                <FormInputText
                                    label={t("common:confirmNewPassword")}
                                    fullWidth={true}
                                    isPassword={true}
                                    placeholder={t("common:confirmNewPassword")}
                                    onChange={(e) => {
                                        if (!e || !e.target || e.target === null) return;

                                        setRequestPwd(
                                            produce((draft) => {
                                                draft!.passwordConfirm = e.target.value;
                                            })
                                        );
                                    }}
                                    validationProps={{
                                        formId: "",
                                        fieldId: "passwordConfirm",
                                        required: false,
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ width: "100%", mt: 2 }}
                        onClick={handleChangePassword}
                        disabled={
                            !requestPwd.oldPassword ||
                            !requestPwd.password ||
                            !requestPwd.passwordConfirm ||
                            requestPwd.password !== requestPwd.passwordConfirm ||
                            !formValid
                        }
                    >
                        {t("button:confirmChangePassword")}
                    </Button>
                </Collapse>
            </Box>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">{t("common:theme")}</Typography>
                    <Select value={userTheme} onChange={handleChangeTheme}>
                        <MenuItem value="light">{t("common:light")}</MenuItem>
                        <MenuItem value="dark">{t("common:dark")}</MenuItem>
                    </Select>
                </Stack>
            </Box>
            {cacheService.IsDriveEnabled() && (
                <>
                    <Divider sx={{ mt: 3, mb: 3 }} />
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="h5">{t("modal:modalUserSettings.body.driveDefaultMode")}</Typography>
                            <Select value={user.prefs?.showMode ?? ShowMode.Thumbs} onChange={handleChangeShowMode}>
                                <MenuItem value="Thumbs">{t("common:thumbnails")}</MenuItem>
                                <MenuItem value="Table">{t("common:table")}</MenuItem>
                            </Select>
                        </Stack>
                    </Box>
                </>
            )}
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
                    <Typography variant="h5">{t("common:language")}</Typography>
                    <Select value={i18n.language} onChange={handleChangeLanguage}>
                        <MenuItem value={Language.English}>{t("common:english")}</MenuItem>
                        <MenuItem value={Language.French}>{t("common:french")}</MenuItem>
                    </Select>
                </Stack>
            </Box>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Typography variant="h5" mb={2}>
                {t("common:features")}
            </Typography>
            <Stack spacing={1}>
                {GlobalConst.UI_FEATURES.length > 2 && (
                    <StyledNotificationCard variant="outlined" checked={isAllFeaturesChecked}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    onChange={(e, checked) => onChangeAllFeatures(checked)}
                                    checked={isAllFeaturesChecked}
                                />
                            }
                            label={t("common:allFeatures")}
                            labelPlacement="start"
                        />
                    </StyledNotificationCard>
                )}
                {GlobalConst.UI_FEATURES.map((feature) => (
                    <StyledNotificationCard key={feature} variant="outlined" checked={isFeatureChecked(feature)}>
                        <FormControlLabel
                            control={
                                <Switch checked={isFeatureChecked(feature)} size="small" onChange={() => checkFeature(feature)} />
                            }
                            label={feature}
                            labelPlacement="start"
                        />
                    </StyledNotificationCard>
                ))}
            </Stack>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Typography variant="h5" mb={2}>
                {t("modal:modalUserSettings.body.notificationSettings")}
            </Typography>
            <Stack spacing={1}>
                <StyledNotificationCard variant="outlined" checked={isAllNotificationsChecked}>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                onChange={(e, checked) => onChangeAllNotifications(checked)}
                                checked={isAllNotificationsChecked}
                            />
                        }
                        label={t("common:allNotifications")}
                        labelPlacement="start"
                    />
                </StyledNotificationCard>
                {notifications.map((notification) => (
                    <StyledNotificationCard
                        key={notification.k}
                        variant="outlined"
                        checked={isNotificationChecked(notification.k)}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isNotificationChecked(notification.k)}
                                    size="small"
                                    onChange={() => checkNotification(NotificationUserSubscribe[notification.k])}
                                />
                            }
                            label={notification.label}
                            labelPlacement="start"
                        />
                    </StyledNotificationCard>
                ))}
            </Stack>
            {dragOrderLists && Object.keys(dragOrderLists) && Object.keys(dragOrderLists).length > 0 && (
                <>
                    <Divider sx={{ mt: 3, mb: 3 }} />
                    <Stack mb={2} direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                        <Stack flexDirection={"row"}>
                            <Typography mr={2} variant="h5">
                                {t("modal:modalUserSettings.body.dragDropLayoutOrders")}
                            </Typography>
                            <Tooltip title={t("tooltip:title.draggableSections")}>
                                <InfoOutlined color="primary" />
                            </Tooltip>
                        </Stack>
                        {Object.keys(dragOrderLists).length > 1 && (
                            <Button
                                disabled={
                                    Object.keys(dragOrderLists)
                                        .map((x) => dragOrderLists[x])
                                        .find((x) => x && x.length > 0) === undefined
                                }
                                onClick={() => {
                                    Object.keys(dragOrderLists).forEach((x) => handleClearDragOrderList(x));
                                }}
                            >
                                {t("button:clearAll")}
                            </Button>
                        )}
                    </Stack>
                    <Stack spacing={1}>
                        {Object.keys(dragOrderLists).map((key) => (
                            <Stack width={"100%"} direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                                <Typography>{key}</Typography>
                                <Button onClick={(e) => handleClearDragOrderList(key)} disabled={!(dragOrderLists[key]?.length > 0)}>
                                    {t("common:clear")}
                                </Button>
                            </Stack>
                        ))}
                    </Stack>
                </>
            )}

            {/* {columnOrdersTable && Object.keys(columnOrdersTable) && Object.keys(columnOrdersTable).length > 0 && (
        <>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Stack mb={2} direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Stack flexDirection={"row"}>
              <Typography mr={2} variant="h5">
                {t("modal:modalUserSettings.body.columnTableOrders")}
              </Typography>
              <Tooltip title={t("tooltip:title.customColumnsOrder")}>
                <InfoOutlined color="primary" />
              </Tooltip>
            </Stack>
            {Object.keys(columnOrdersTable).length > 1 && (
              <Button
                disabled={
                  Object.keys(columnOrdersTable)
                    .map((x) => columnOrdersTable[x])
                    .find((x) => x && x.length > 0) === undefined
                }
                onClick={() => {
                  Object.keys(columnOrdersTable).forEach((x) => handleClearcolumnOrdersTable(x));
                }}
              >
                {t("button:clearAll")}
              </Button>
            )}
          </Stack>
          <Stack spacing={1}>
            {Object.keys(columnOrdersTable).map((key) => (
              <Stack width={"100%"} direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                <Typography>{key}</Typography>
                <Button
                  onClick={(e) => handleClearcolumnOrdersTable(key)}
                  disabled={!(columnOrdersTable[key]?.length > 0)}
                >
                  {t("common:clear")}
                </Button>
              </Stack>
            ))}
          </Stack>
        </>
      )} */}
        </Modal>
    );
});

export default ModalUserSettings;

const StyledNotificationCard = styled(Card, { shouldForwardProp: (prop) => prop !== "checked" })<{
    checked?: boolean;
}>(({ theme, checked }) => ({
    padding: theme.spacing(2),
    opacity: checked ? 1 : 0.5,
    transition: "opacity .3s",
    ".MuiFormControlLabel-root": {
        justifyContent: "space-between",
        width: "100%",
        marginLeft: 0,
        marginRight: 0,
    },
}));
